import {
  Check, Play, Sparkles, Send, Save, FlaskConical,
  ExternalLink, Eye, Search, RefreshCw,
  Trash2, X, Archive, PowerOff,
  type LucideIcon,
} from "lucide-react";
import type { ButtonVariant } from "@/components/ui/button";

/**
 * Canonical operator-action vocabulary.
 *
 * Every page in the app picks actions from this catalog instead of
 * choosing icons / variants / labels / confirmation copy ad-hoc, so
 * "Approve" looks identical (tone, icon, button variant, hover state)
 * on Issues, Patches, Inbox, Follow-ups, and any future surface that
 * surfaces it.
 *
 * Three tiers, each with fixed visual treatment:
 *
 *   Primary  — `<Button variant="primary">` — the dominant CTA.
 *              At most one per surface (toolbar / sticky action bar /
 *              modal footer). The catalog enforces this by tier;
 *              callers can't accidentally promote two actions to
 *              brand-gradient prominence.
 *
 *   Secondary — `<Button variant="secondary">` — Open / View /
 *              Inspect / Retry. Same chrome as the SearchInput,
 *              Select, and toolbar utility buttons so they slot in.
 *
 *   Danger   — `<Button variant="danger">` — Delete / Discard /
 *              Ignore / Disable. `requiresConfirm` triggers the
 *              ConfirmModal flow with the catalog's standard copy.
 *
 * The label is a SUGGESTION; pages override per locale (`tr(...)`)
 * since the catalog itself is i18n-agnostic. The visual treatment
 * (icon, variant, destructive, confirm flag) is NOT overridable
 * — that's the whole point.
 */

export type ActionTier = "primary" | "secondary" | "danger";

export type ActionKey =
  // Primary
  | "approve" | "runFix" | "convert" | "send" | "save"
  // Secondary
  | "open" | "view" | "inspect" | "retry" | "dryRun"
  // Danger
  | "delete" | "discard" | "ignore" | "disable";

export type ActionConfirmCopy = {
  /** Modal title — single short sentence. */
  title: string;
  /** Confirm button label — verb form ("Delete", "Discard"). */
  confirmLabel: string;
  /** Body template; the page composes the entity-specific detail. */
  bodyHint: string;
};

export type ActionDescriptor = {
  key: ActionKey;
  tier: ActionTier;
  /** Canonical icon. Pages don't pick icons for these actions —
   *  they live in the catalog so "Delete" is always Trash2. */
  icon: LucideIcon;
  /** Maps to ButtonVariant. Tier already implies variant; the field
   *  is here so consumers don't have to know the mapping. */
  variant: ButtonVariant;
  /** Default English label. Pages translate; field exists so non-i18n
   *  call sites still render something sensible. */
  defaultLabel: string;
  /** True for irreversible actions. The `<ConfirmAction>` wrapper
   *  reads this and refuses to run without the ConfirmModal. */
  destructive: boolean;
  /** When set, `<ConfirmAction>` uses this copy instead of inventing
   *  its own. Optional — non-destructive actions don't need it. */
  confirm?: ActionConfirmCopy;
};

export const ACTIONS: Record<ActionKey, ActionDescriptor> = {
  // ── Primary ────────────────────────────────────────────────────
  approve: {
    key: "approve", tier: "primary", icon: Check, variant: "primary",
    defaultLabel: "Approve", destructive: false,
  },
  runFix: {
    key: "runFix", tier: "primary", icon: Play, variant: "primary",
    defaultLabel: "Run fix", destructive: false,
  },
  convert: {
    key: "convert", tier: "primary", icon: Sparkles, variant: "primary",
    defaultLabel: "Convert", destructive: false,
  },
  send: {
    key: "send", tier: "primary", icon: Send, variant: "primary",
    defaultLabel: "Send", destructive: false,
  },
  save: {
    key: "save", tier: "primary", icon: Save, variant: "primary",
    defaultLabel: "Save", destructive: false,
  },

  // ── Secondary ──────────────────────────────────────────────────
  open: {
    key: "open", tier: "secondary", icon: ExternalLink, variant: "secondary",
    defaultLabel: "Open", destructive: false,
  },
  view: {
    key: "view", tier: "secondary", icon: Eye, variant: "secondary",
    defaultLabel: "View", destructive: false,
  },
  inspect: {
    key: "inspect", tier: "secondary", icon: Search, variant: "secondary",
    defaultLabel: "Inspect", destructive: false,
  },
  retry: {
    key: "retry", tier: "secondary", icon: RefreshCw, variant: "secondary",
    defaultLabel: "Retry", destructive: false,
  },
  // Dry run is the canonical "preview before apply" verb. Lives at
  // the secondary tier so it never visually outweighs Run fix when
  // both appear together; pages still surface a primary `<RunFix>`
  // (or use it directly when `assess*Risk()` returns level=high).
  dryRun: {
    key: "dryRun", tier: "secondary", icon: FlaskConical, variant: "secondary",
    defaultLabel: "Dry run", destructive: false,
  },

  // ── Danger ─────────────────────────────────────────────────────
  // `Delete` / `Discard` are irreversible — they always run through
  // ConfirmModal. `Ignore` / `Disable` are reversible (the operator
  // can re-enable / re-process), so they don't strictly need a
  // confirm — but the catalog defaults `requiresConfirm` to true
  // anyway because operator surveys consistently flag accidental
  // ignore/disable as a top frustration. Pages can opt out by passing
  // `confirm={false}` to <ConfirmAction>.
  delete: {
    key: "delete", tier: "danger", icon: Trash2, variant: "danger",
    defaultLabel: "Delete", destructive: true,
    confirm: {
      title: "Delete this item?",
      confirmLabel: "Delete",
      // NOT "this can't be undone": the governed entities this verb is used
      // on (issues, workspaces, projects, codebases) are soft-deleted —
      // hidden, restorable by an admin, and their audit history, approval
      // decisions and verification evidence are retained. Call sites that
      // delete something genuinely irreversible pass their own `body`.
      bodyHint: "It leaves active work. The governance history FixControl already recorded is kept.",
    },
  },
  discard: {
    key: "discard", tier: "danger", icon: X, variant: "danger",
    defaultLabel: "Discard", destructive: true,
    confirm: {
      title: "Discard changes?",
      confirmLabel: "Discard",
      bodyHint: "Unsaved changes will be lost.",
    },
  },
  ignore: {
    key: "ignore", tier: "danger", icon: Archive, variant: "danger",
    defaultLabel: "Ignore", destructive: true,
    confirm: {
      title: "Ignore this item?",
      confirmLabel: "Ignore",
      bodyHint: "It will be hidden from the active queue. You can restore it from the All view.",
    },
  },
  disable: {
    key: "disable", tier: "danger", icon: PowerOff, variant: "danger",
    defaultLabel: "Disable", destructive: true,
    confirm: {
      title: "Disable this item?",
      confirmLabel: "Disable",
      bodyHint: "It will stop processing until you re-enable it.",
    },
  },
};

/** Convenience accessor — throws on unknown key (caller bug). */
export function getAction(key: ActionKey): ActionDescriptor {
  const d = ACTIONS[key];
  if (!d) throw new Error(`Unknown action key: ${key}`);
  return d;
}
